import { useCallback, useState } from "react";
import BreadCrumb from "components/common/BreadCrumb";
import TableContainer from "components/common/TableContainer";
import { useProfile } from "features/Authentication/hooks/useProfile";
import { AsyncPaginatedSelect } from "components/common/AsyncPaginatedSelect";
import { getDocumentTitle } from "utils/helpers";
import { getAttendanceReport } from "features/EmployeeAttendance/services/EmployeeAttendanceService";
import { getEmployeeOptions } from "features/Employees/services/EmployeeService";
import type {
  AttendanceReportRow,
  AttendanceReportSummary,
} from "features/EmployeeAttendance/types/employee-attendance.types";
import type { SelectOption } from "types/api.types";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const now = new Date();
const YEAR_OPTIONS: number[] = Array.from(
  { length: 8 },
  (_, i) => now.getFullYear() - 2 + i,
);

function fmtOT(minutes: number): string {
  if (!minutes) return "0h";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

const COLUMNS: ColumnDef<AttendanceReportRow>[] = [
  {
    header: "Employee",
    accessorKey: "employee",
    enableColumnFilter: false,
    enableSorting: false,
    size: 200,
    cell: ({ row }) => {
      const emp = row.original.employee;
      return (
        <div>
          <Link to={`/employees/${emp.id}`} className="fw-medium text-body">
            {emp.name ?? `#${emp.id}`}
          </Link>
          {emp.code && <div className="text-muted small">{emp.code}</div>}
        </div>
      );
    },
  },
  {
    header: "Present",
    accessorKey: "present",
    enableColumnFilter: false,
    enableSorting: false,
    cell: ({ getValue }) => (
      <span className="fw-semibold text-success">{getValue() as number}</span>
    ),
  },
  {
    header: "Half Day",
    accessorKey: "halfDay",
    enableColumnFilter: false,
    enableSorting: false,
    cell: ({ getValue }) => (
      <span className="fw-semibold text-warning">{getValue() as number}</span>
    ),
  },
  {
    header: "Absent",
    accessorKey: "absent",
    enableColumnFilter: false,
    enableSorting: false,
    cell: ({ getValue }) => (
      <span className="fw-semibold text-danger">{getValue() as number}</span>
    ),
  },
  {
    header: "Leave",
    accessorKey: "leave",
    enableColumnFilter: false,
    enableSorting: false,
    cell: ({ getValue }) => (
      <span className="fw-semibold text-info">{getValue() as number}</span>
    ),
  },
  {
    header: "Working days",
    accessorKey: "totalWorkingDays",
    enableColumnFilter: false,
    enableSorting: false,
    cell: ({ getValue }) => (
      <span className="tabular-nums">{getValue() as number}</span>
    ),
  },
  {
    header: "Effective days",
    accessorKey: "effectiveWorkingDays",
    enableColumnFilter: false,
    enableSorting: false,
    cell: ({ getValue }) => (
      <span className="tabular-nums">{getValue() as number}</span>
    ),
  },
  {
    header: "Overtime",
    accessorKey: "totalOvertimeMinutes",
    enableColumnFilter: false,
    enableSorting: false,
    cell: ({ getValue }) => (
      <span className="tabular-nums text-muted">
        {fmtOT(getValue() as number)}
      </span>
    ),
  },
];

const SUMMARY_ITEMS: {
  key: keyof AttendanceReportSummary;
  label: string;
  cls: string;
}[] = [
  { key: "present", label: "Present", cls: "text-success" },
  { key: "halfDay", label: "Half Day", cls: "text-warning" },
  { key: "absent", label: "Absent", cls: "text-danger" },
  { key: "leave", label: "Leave", cls: "text-info" },
  { key: "holidayCount", label: "Holidays", cls: "text-muted" },
  { key: "totalWorkingDays", label: "Working days", cls: "text-body" },
];

function buildCsv(rows: AttendanceReportRow[]): string {
  const UTF8_BOM = "﻿";
  const esc = (v: string | null | undefined) => {
    const s = v ?? "";
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const header = [
    "Employee ID",
    "Code",
    "Name",
    "Email",
    "Department",
    "Designation",
    "Present",
    "Half Day",
    "Absent",
    "Leave",
    "Working Days",
    "Effective Days",
    "Overtime (min)",
  ];
  const lines = [header.join(",")];

  for (const r of rows) {
    lines.push(
      [
        String(r.employee.id),
        esc(r.employee.code),
        esc(r.employee.name),
        esc(r.employee.email),
        esc(r.employee.department),
        esc(r.employee.designation),
        String(r.present),
        String(r.halfDay),
        String(r.absent),
        String(r.leave),
        String(r.totalWorkingDays),
        String(r.effectiveWorkingDays),
        String(r.totalOvertimeMinutes),
      ].join(","),
    );
  }

  return UTF8_BOM + lines.join("\n");
}

function downloadCsv(content: string, month: number, year: number) {
  const mm = String(month).padStart(2, "0");
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `attendance_report_${year}_${mm}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const AttendanceReportPage = () => {
  document.title = getDocumentTitle("Attendance report");

  const { isSuperAdmin, isHR } = useProfile();
  const showBroadFilters = isSuperAdmin || isHR;

  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [employeeId, setEmployeeId] = useState<number | null>(null);
  const [summary, setSummary] = useState<AttendanceReportSummary | null>(null);
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await getAttendanceReport({
        month,
        year,
        employeeId,
        page: 1,
        limit: 9999,
      });
      const rows = (res.data?.rows ?? []) as AttendanceReportRow[];
      if (!rows.length) {
        toast.info("No data to export");
        return;
      }
      downloadCsv(buildCsv(rows), month, year);
      toast.success("Report downloaded");
    } catch {
      toast.error("Could not export report");
    } finally {
      setExporting(false);
    }
  };

  const fetchApi = useCallback(
    async ({ page, limit }: { page: number; limit: number }) => {
      const res = await getAttendanceReport({
        month,
        year,
        employeeId,
        page,
        limit,
      });
      if (res.success && res.data) {
        const raw = res.data as typeof res.data & {
          summary?: Omit<AttendanceReportSummary, "holidayCount">;
          holidayCount?: number;
        };
        if (raw.summary)
          setSummary({ ...raw.summary, holidayCount: raw.holidayCount ?? 0 });
      }
      return res;
    },
    [month, year, employeeId],
  );

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Attendance report" pageTitle="Reports" />
        <Card>
          <CardHeader className="border-bottom d-flex flex-wrap justify-content-between align-items-center gap-2">
            <div>
              <h5 className="card-title mb-1">Attendance report</h5>
              <p className="text-muted mb-0 small">
                Monthly attendance summary per employee — present, absent,
                leave, and other day types.
              </p>
            </div>
            <Button
              color="light"
              className="btn-label"
              disabled={exporting}
              onClick={() => void handleExport()}
            >
              {exporting ? (
                <Spinner size="sm" className="label-icon" />
              ) : (
                <i className="ri-download-2-line label-icon align-middle fs-16" />
              )}
              Export CSV
            </Button>
          </CardHeader>
          <CardBody className="pt-3">
            {/* Filters */}
            <Row className="g-3 mb-4 align-items-end">
              <Col xs={6} sm={4} md={2}>
                <Label className="form-label mb-1">Month</Label>
                <Input
                  type="select"
                  bsSize="sm"
                  value={month}
                  onChange={(e) => setMonth(Number(e.target.value))}
                >
                  {MONTH_NAMES.map((name, i) => (
                    <option key={i + 1} value={i + 1}>
                      {name}
                    </option>
                  ))}
                </Input>
              </Col>
              <Col xs={6} sm={3} md={2}>
                <Label className="form-label mb-1">Year</Label>
                <Input
                  type="select"
                  bsSize="sm"
                  value={year}
                  onChange={(e) => setYear(Number(e.target.value))}
                >
                  {YEAR_OPTIONS.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </Input>
              </Col>
              {showBroadFilters ? (
                <Col xs={12} sm={6} md={4} lg={3}>
                  <Label className="form-label mb-1">Employee</Label>
                  <AsyncPaginatedSelect<SelectOption>
                    value={employeeId}
                    fetchOptions={getEmployeeOptions}
                    placeholder="All employees"
                    onChange={(opt) =>
                      setEmployeeId(
                        opt ? ((opt as SelectOption).value as number) : null,
                      )
                    }
                  />
                </Col>
              ) : null}
            </Row>

            {/* Summary bar */}
            {summary && (
              <Row className="g-2 mb-4">
                {SUMMARY_ITEMS.map(({ key, label, cls }) => (
                  <Col key={key} xs={6} sm={4} md={2}>
                    <div className="p-3 rounded border bg-white text-center">
                      <div className={`fs-4 fw-bold ${cls}`}>
                        {summary[key] as number}
                      </div>
                      <div className="text-muted small mt-1">{label}</div>
                    </div>
                  </Col>
                ))}
              </Row>
            )}

            <TableContainer<AttendanceReportRow>
              columns={COLUMNS}
              fetchApi={fetchApi}
              pageSize={20}
              globalSearch={false}
              divClass="table-responsive table-card mb-0"
              tableClass="align-middle table-nowrap mb-0"
              theadClass="table-light text-muted"
            />
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default AttendanceReportPage;
